import * as React from "react";
import { useCallback } from "react";
import { Box, Typography, Table, TableBody, TableHead, TableRow, TableCell, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import translations from "translations";
import Props from "./index.d";
import { StyledTableContainer, StyledColumnHeaderCell, StyledRowHeaderCell, StyledTextField } from "./styledComponents";
import { useMatrixFormState } from "../MatrixParameters/useMatrixFormState";
import { InertiaParametersData } from "pages/UploadCertificate/types";
import { useMatrixParameterVisibility } from "../hooks/useMatrixParameterVisibility";
import MatrixParameterVisibilityModal from "../MatrixParameterVisibilityModal";
import { subscript } from "components/mlpaUiComponents/LegalNote/lib";

const parseInertiaState = (state: string | undefined): InertiaParametersData => {
  if (state && typeof state === "string" && state.trim() !== "") {
    try {
      return JSON.parse(state) as InertiaParametersData;
    } catch {
      // ignore
    }
  }
  return {};
};

const MatrixParametersType1: React.FC<Props> = (props) => {
  const {
    title,
    data: { state, setstate },
    disabled = false,
    errorManager,
    className,
    getFieldValue,
  } = props;

  const {
    showVisibilityControls,
    openVisibilityModal,
    setOpenVisibilityModal,
    hasVisibilityGrants,
    getVisibilityTooltip,
    handleVisibilitySave,
    currentRoles,
    currentTenantIds,
  } = useMatrixParameterVisibility({ getFieldValue });

  const errorManagerRes = errorManager({
    errorJoiner: (errors) => errors.join(", "),
  });

  const {
    formData,
    setFormData,
    setTrailingChars,
    hasUserEditedRef,
    validateAndParseInput,
    getDisplayValue,
    clearTrailingChars,
  } = useMatrixFormState<InertiaParametersData>({
    state,
    setstate,
    parseState: parseInertiaState,
  });

  // Sum validation: taGen + taZus should equal taGes
  const sumError = React.useMemo(() => {
    const toNum = (v: string | number | undefined) => (v !== undefined && v !== "" ? Number(v) : null);
    const gen = toNum(formData.taGen);
    const zus = toNum(formData.taZus);
    const ges = toNum(formData.taGes);
    if (gen === null || zus === null || ges === null) return null;
    if (isNaN(gen) || isNaN(zus) || isNaN(ges)) return null;
    return Math.abs(gen + zus - ges) > 0.0005
      ? translations.pages.certificateUpload.matrixParametersType1.sumValidationError
      : null;
  }, [formData]);

  const handleChange = useCallback(
    (field: keyof InertiaParametersData, inputValue: string) => {
      const currentValue = formData[field];
      const currentNumeric = typeof currentValue === "number" ? currentValue : undefined;
      const validation = validateAndParseInput(inputValue, currentNumeric);

      if (validation.isValid) {
        // parsedValue is undefined when the input is non-numeric (e.g. letters) — reject it
        if (validation.parsedValue === undefined && inputValue !== "") return;
        hasUserEditedRef.current = true;
        setTrailingChars((prev) => ({ ...prev, [field]: validation.trailingChars }));
        const newValue =
          inputValue === "" ? "" : validation.parsedValue !== undefined ? validation.parsedValue : inputValue;
        setFormData((prev) => ({ ...prev, [field]: newValue }));
      }
    },
    [formData, validateAndParseInput, hasUserEditedRef, setTrailingChars, setFormData]
  );

  const t = translations.pages.certificateUpload.matrixParametersType1;

  const rows: { field: keyof InertiaParametersData; label: string; unit: string }[] = [
    { field: "taGen", label: `${t.taGenLabel} T${subscript("A,gen")}`, unit: `[s]` },
    { field: "taZus", label: `${t.taZusLabel} T${subscript("A,zus")}`, unit: `[s]` },
    { field: "taGes", label: `${t.taGesLabel} T${subscript("A,ges")}`, unit: `[s]` },
  ];

  return (
    <Box className={className} style={{ opacity: disabled ? 0.6 : 1 }} sx={{ backgroundColor: "white", p: 2 }}>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}

      {/* Visibility control - only show during upload, not edit */}
      {showVisibilityControls && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, pl: "16px", pt: 0.5 }}>
          <Typography sx={{ minWidth: "200px", fontSize: "18px", fontWeight: 400, color: "text.secondary" }}>
            {translations.pages.certificateUpload.matrixVisibility.configureAccess}:
          </Typography>
          <Tooltip title={getVisibilityTooltip()}>
            <IconButton disableRipple onClick={() => setOpenVisibilityModal(true)} size="small">
              {hasVisibilityGrants() ? <VisibilityIcon sx={{ color: "primary.main" }} /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <StyledTableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledColumnHeaderCell sx={{ width: "55%" }}>{t.labelColumnHeader}</StyledColumnHeaderCell>
              <StyledColumnHeaderCell sx={{ width: "25%", textAlign: "center" }}>
                {t.valueColumnHeader}
              </StyledColumnHeaderCell>
              <StyledColumnHeaderCell sx={{ width: "20%", textAlign: "center" }}>
                {t.unitColumnHeader}
              </StyledColumnHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ field, label, unit }) => {
              const isError = field === "taGes" && sumError !== null;
              return (
                <TableRow key={field}>
                  <StyledRowHeaderCell dangerouslySetInnerHTML={{ __html: label }} />
                  <TableCell sx={{ p: "8px 16px" }}>
                    <StyledTextField
                      value={getDisplayValue(field, formData[field])}
                      onChange={(e) => handleChange(field, e.target.value)}
                      onBlur={() => clearTrailingChars(field)}
                      disabled={disabled}
                      error={isError}
                      inputProps={{ type: "text", inputMode: "decimal" }}
                    />
                    {/* {isError && sumError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 0.5 }}
                        dangerouslySetInnerHTML={{ __html: sumError }}
                      />
                    )} */}
                  </TableCell>
                  <StyledRowHeaderCell sx={{ textAlign: "center" }} dangerouslySetInnerHTML={{ __html: unit }} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {errorManagerRes.error && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 1 }}
          dangerouslySetInnerHTML={{ __html: `${errorManagerRes.helperText}` }}
        />
      )}
      <MatrixParameterVisibilityModal
        open={openVisibilityModal}
        currentRoles={currentRoles}
        currentTenantIds={currentTenantIds}
        onClose={() => setOpenVisibilityModal(false)}
        onSave={handleVisibilitySave}
      />
    </Box>
  );
};

export default MatrixParametersType1;
